import React, { FC, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Modal, Spin, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { requestGetMotionAlertsInvoiceRequest, requestMotionAlertsInvoice } from 'pages/ActivityFeed/services';
import { ICustomMap } from 'types/ICustomMap';
import { IWorkspace } from 'pages/CommandCenter/Accounts/types';
import { textsActivityFeed, textsCommon } from 'texts/index';
import Button from 'components/antd/Button';
import ButtonsRow from 'components/common/button/ButtonsRow';
import styles from './styles.module.scss';

export const motionAlertsInvoiceRequestQueryKey = 'motion-alerts-request-invoice-status';

export type MotionDetectionPremiumModalProps = {
    open: boolean;
    onClose: () => void;
    isMotionAlertsPaid: boolean;
};

const MotionDetectionPremiumModal: FC<MotionDetectionPremiumModalProps> = ({ open, onClose, isMotionAlertsPaid }) => {
    const workspace = useSelector(({ workspace: ws }: ICustomMap) => ws as IWorkspace);
    const queryClient = useQueryClient();

    const premiumItems = useMemo(
        () =>
            [
                textsActivityFeed.motionDetectionPremiumItem1,
                textsActivityFeed.motionDetectionPremiumItem2,
                textsActivityFeed.motionDetectionPremiumItem3,
                textsActivityFeed.motionDetectionPremiumItem4,
            ].filter(item => !!item),
        [],
    );

    const {
        data: invoiceRequestData,
        isLoading: isLoadingInvoiceRequest,
        isError: isInvoiceRequestError,
    } = useQuery({
        queryKey: [motionAlertsInvoiceRequestQueryKey, workspace?.id_str],
        queryFn: () => requestGetMotionAlertsInvoiceRequest(),
        enabled: open && !isMotionAlertsPaid && !!workspace?.id_str,
        retry: 1,
    });

    const invoiceRequestInProgress = !isInvoiceRequestError && !!invoiceRequestData?.respData?.invoice_request_in_progress;

    const { mutate: requestInvoice, isLoading: isSubmittingRequest } = useMutation(requestMotionAlertsInvoice, {
        onSuccess: () => {
            queryClient.invalidateQueries([motionAlertsInvoiceRequestQueryKey, workspace?.id_str]);
            onClose();
        },
    });

    const handleEnableClick = useCallback(() => {
        if (!workspace?.id_str) return;
        requestInvoice({ notes: '' });
    }, [requestInvoice, workspace?.id_str]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <Modal
            open={open}
            title={textsActivityFeed.motionDetectionPremiumTitle}
            onCancel={isSubmittingRequest ? undefined : handleClose}
            closable={!isSubmittingRequest}
            footer={null}
        >
            {isLoadingInvoiceRequest && (
                <div className={styles.invoiceRequestSpin}>
                    <Spin />
                </div>
            )}
            {!isLoadingInvoiceRequest && invoiceRequestInProgress && (
                <>
                    <p>{textsActivityFeed.motionDetectionInvoiceRequestInProgress}</p>
                    <ButtonsRow>
                        <Button type='primary' size='large' onClick={handleClose}>
                            {textsCommon.buttons.close}
                        </Button>
                    </ButtonsRow>
                </>
            )}
            {!isLoadingInvoiceRequest && !invoiceRequestInProgress && (
                <>
                    <p>{textsActivityFeed.motionDetectionPremiumIntro}</p>
                    {textsActivityFeed.motionDetectionPremiumWhatsIncluded && (
                        <p>{textsActivityFeed.motionDetectionPremiumWhatsIncluded}</p>
                    )}
                    <ul>
                        {premiumItems.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                    <p>
                        {textsActivityFeed.motionDetectionPremiumPricingLead}{' '}
                        <Typography.Text strong>{textsActivityFeed.motionDetectionPremiumPricing}</Typography.Text>
                    </p>
                    <p>{textsActivityFeed.motionDetectionPremiumCtaDescription}</p>
                    <ButtonsRow>
                        <Button type='primary' size='large' loading={isSubmittingRequest} onClick={handleEnableClick}>
                            {textsActivityFeed.motionDetectionRequestEnable}
                        </Button>
                        <Button onClick={handleClose} disabled={isSubmittingRequest} size='large'>
                            {textsCommon.buttons.cancel}
                        </Button>
                    </ButtonsRow>
                </>
            )}
        </Modal>
    );
};

export default MotionDetectionPremiumModal;
